/**
 * OrderTifCell — the Open Orders table's side + time-in-force cell (#517).
 *
 * Lifted verbatim out of `index.tsx`, where it was an inline IIFE roughly 6,500
 * lines into the file, holding its own copy of the GTS tolerance. The card had
 * the same arithmetic in `orderCardModel`. #517 requires the card and this table
 * to agree about an expiry that precedes the event start, and two independent
 * copies of a comparison agree only until someone edits one — so the decision
 * now comes from the shared predicates and only the SHAPE differs here.
 *
 * That is also what makes the behavior testable at all. Mounted directly, every
 * expiry case can be driven in a few lines; inside `index.tsx` the same cases
 * needed the whole tracker, its stores and an authenticated session, which is
 * why the surface had no coverage before.
 *
 * ── Why a marker, not the notice box ────────────────────────────────
 * `ExpiryBeforeStartWarning` draws a bordered box with three lines of prose.
 * That belongs on a card and would wreck a table row, so the row renders that
 * module's OTHER shape, `ExpiryBeforeStartMarker` — same words, same amber,
 * same claim, different geometry. #557 moved the marker's markup there from
 * here, because the Polymarket rows needed the identical thing and a second
 * hand-built copy is how two surfaces start disagreeing.
 *
 * ── The amber already in this cell ──────────────────────────────────
 * `In sale` is an uppercase amber PILL with a filled background and means
 * something entirely different: this order is filling right now. Colour is
 * load-bearing on this page, so the new marker deliberately takes a different
 * form — bare icon plus lowercase text, no pill — and is appended AFTER the
 * pill so nothing already on the row moves. Both can be on at once: an order
 * can be partially filled and still doomed to expire pre-game.
 */

import type { PolyRelayOpenOrder } from '../../lib/polymarket/relay';
import { ExpiryBeforeStartMarker } from '../../components/atoms/ExpiryBeforeStartWarning';
import {
  expiryPrecedesEventStart,
  isGtsEquivalentExpiry,
  polyExpirationMs,
  polyTifLabel,
} from '../../components/cards/orderCardModel';

export function OrderTifCell({
  action,
  expirationTs,
  eventStartMs,
  activeInSale = false,
}: {
  action: string | null | undefined;
  /** Venue expiration in unix SECONDS. Absent/null = GTC. */
  expirationTs: number | null | undefined;
  /** The event start G1 currently shows for this order's market. */
  eventStartMs: number | null | undefined;
  activeInSale?: boolean;
}) {
  const sideLabel = action === 'sell' ? 'ASK' : 'BID';

  // No expiration → GTC (rests until cancel or close). An expiration that lines
  // up with the scheduled event start (±60s for the ISO→sec rounding) → GTS.
  // Anything else → the local time, which is what a frozen GTS expiry becomes
  // once the schedule moves out from under it.
  let tifLabel = 'GTC';
  let tifTitle = 'Good til close — rests until cancel or contract settlement';
  let expiresBeforeEventStart = false;

  if (expirationTs != null) {
    const expMs = expirationTs * 1000;
    const exact = new Date(expMs).toLocaleString();
    if (isGtsEquivalentExpiry(expMs, eventStartMs)) {
      tifLabel = 'GTS';
      // Says "captured at placement", NOT "auto-cancels at scheduled event
      // start". The old wording implied the venue tracks the schedule; #510
      // proved it does not, and that implication is what left a trader unable
      // to explain a vanished order.
      tifTitle = `Good to Start — fixed venue expiry captured from the scheduled start when the order was placed, and not moved by later schedule changes (${exact})`;
    } else {
      tifLabel = new Date(expMs).toLocaleTimeString([], {
        hour: 'numeric',
        minute: '2-digit',
      });
      tifTitle = `Auto-cancels at ${exact}`;
    }
    expiresBeforeEventStart = expiryPrecedesEventStart(expMs, eventStartMs);
  }

  return (
    <span title={tifTitle}>
      <span className="text-foreground">{sideLabel}</span>
      <span className="text-muted-foreground ml-1">({tifLabel})</span>
      {activeInSale && (
        <span className="ml-1 rounded bg-amber-400/20 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-300">
          In sale
        </span>
      )}
      <ExpiryBeforeStartMarker expiresBeforeEventStart={expiresBeforeEventStart} />
    </span>
  );
}

/**
 * The Polymarket row's side + time-in-force cell (#557).
 *
 * Same job as {@link OrderTifCell}, kept as a SEPARATE component rather than a
 * venue flag on that one, because the two venues genuinely differ in what they
 * hand us and how the label reads:
 *   • Kalshi gives `expiration_ts` as a number; the CLOB gives `expiration` as
 *     a unix-seconds STRING that may also be `'0'`, `null`, or absent for GTC.
 *     `polyExpirationMs` owns that decoding, and nothing else should.
 *   • The label formats differ on purpose — this row reads "3:35p" (the poly
 *     surfaces' clock, matching the start beside it) where the Kalshi cell
 *     reads "3:35 PM". #557 preserves both.
 * What must NOT differ is the finding, so both cells derive it from
 * `expiryPrecedesEventStart` and render the same `ExpiryBeforeStartMarker`.
 *
 * Lifted out of the inline markup ~6,250 lines into `index.tsx` for the same
 * reason `OrderTifCell` was: inline, the poly row's expiry behavior could not
 * be mounted, and so had never been tested at all.
 */
export function PolyOrderTifCell({
  order,
  eventStartMs,
  activeInSale = false,
}: {
  /** The raw CLOB open-order row, as the relay passes it through. */
  order: PolyRelayOpenOrder;
  /** The event start G1 currently shows for this order's market. */
  eventStartMs: number | null | undefined;
  activeInSale?: boolean;
}) {
  const isSell = String(order.side ?? '').toUpperCase() === 'SELL';
  return (
    <>
      <span className="text-foreground">{isSell ? 'ASK' : 'BID'}</span>
      <span className="text-muted-foreground ml-1">
        ({polyTifLabel(order, eventStartMs ?? null)})
      </span>
      {activeInSale && (
        <span className="ml-1 rounded bg-amber-400/20 px-1 py-0.5 text-[9px] font-bold uppercase text-amber-300">
          In sale
        </span>
      )}
      <ExpiryBeforeStartMarker
        expiresBeforeEventStart={expiryPrecedesEventStart(
          polyExpirationMs(order),
          eventStartMs,
        )}
      />
    </>
  );
}
